import type { ReactElement } from 'react';
import { Fragment, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Button, IconButton, PlanBadge, tokens, Tooltip, Badge, Icon } from '@hotjar/hotsauce';
import { Cell, Row } from '@hotjar/webapp/components/GridTable/GridTable';
import { navigateTo } from '@hotjar/webapp/utils/routing';
import { BILLING } from '@hotjar/webapp/constants/routes/settings';
import { SETTINGS } from '@hotjar/webapp/constants/routes/site';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { useTranslation } from 'react-i18next';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { DropdownMenu } from '@hotjar/webapp/components/DropdownMenu';
import { BASE } from '@hotjar/webapp/constants/routes/overview';
import { EnforceSiteMfaSwitch } from '@hotjar/webapp/features/Mfa';
import { useReadableProductName } from '@hotjar/webapp/domains/common/product/hooks/useReadableProductName';
import { ProductType } from '@hotjar/webapp/domains/common/product/types';
import { useAuthorization } from '@hotjar/webapp/domains/authorization';
import { moment } from '@hotjar/webapp/lib/moment';
import { useCurrentUser } from '@hotjar/user';
import { isAccountOwner } from '@hotjar/account';
import { isBillingV3Enabled } from '@hotjar/webapp/domains/billing/features';
import { CHECKOUT } from '~/subscription-management/constants/routes';
import { Event, UiPosition } from '../events';
import { SiteListAction } from './Actions/SiteListAction';
import { SiteDeleteIcon } from './Actions/SiteDeleteIcon';
import { TrackingCodeState } from './TrackingCode/TrackingCodeState';
import type { SiteModel, SiteTrackingData } from './types';
import { DeleteSiteModal } from './DeleteSite/DeleteSiteModal';
import { TransferSiteModal } from './TransferSite/TransferSiteModal';

interface SiteProps {
  site: SiteModel;
  sitesTotal: number;
  isMobile: boolean;
  onTrackingCodeClick(site: SiteModel): void;
  isSSOEnabled: boolean;
}

const TRANSLATION_DOMAIN = 'account:sites-and-organizations.site';

const S = {
  Row: styled(Row)`
    &:last-child td {
      border: none;
    }
  `,
  Cell: styled(Cell)<{ link?: boolean }>`
    display: flex;
    flex-wrap: wrap;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    line-height: 3;
    min-width: 0;

    ${({ link }) => link && `cursor: pointer;`}
  `,
  TruncateText: styled.span`
    flex: 1;
    ${tokens.truncateText()};
  `,
  ChangePlanButton: styled(Button)`
    margin-right: ${tokens.spacer.x3};
  `,
  SettingsIcon: styled.span`
    @media ${tokens.breakpoint.minWidth.m} {
      margin: 0 ${tokens.spacer.x3};
    }
  `,
  Grid: styled.div`
    display: grid;
    grid-template-columns: auto auto;
    grid-column-gap: ${tokens.spacer.x2};
    grid-row-gap: ${tokens.spacer.x1};
    line-height: normal;
    justify-items: start;
  `,
  ActionRequiredBadge: styled(Badge)`
    background-color: ${tokens.BRAND_COLORS.neutral_900};
    margin-right: auto;
  `,
};

const firstOfJuly2022 = moment('2022-07-01');

function getEndOfGracePeriod(account: SiteModel['account']) {
  if (account.billingCycle === 'monthly' || !account.nextBillingDate) {
    return firstOfJuly2022.format('DD MMMM YYYY');
  }

  const billingCycle = moment.unix(account.nextBillingDate);
  const endDate = billingCycle.isBefore(firstOfJuly2022)
    ? billingCycle.add(1, 'year')
    : billingCycle;
  return endDate.format('DD MMMM YYYY');
}

export function Site({
  site,
  sitesTotal,
  isMobile,
  onTrackingCodeClick,
  isSSOEnabled,
}: SiteProps): ReactElement {
  const { t } = useTranslationWithPrefix(TRANSLATION_DOMAIN);
  const { t: tRevenue } = useTranslation('revenue');
  const { click } = useTrack();
  const [deleteSite, setDeleteSite] = useState<boolean>(false);
  const [transferSite, setTransferSite] = useState<boolean>(false);

  const analyticsProductName = useReadableProductName(ProductType.Analytics);
  const feedbackProductName = useReadableProductName(ProductType.Feedback);
  const { hasPermission } = useAuthorization();

  const hasSitesUpdatePermission = hasPermission('site', `${site.id}`, 'sites-update');
  const hasSitesDeletePermission = hasPermission('site', `${site.id}`, 'sites-delete');
  const hasSitePlanUpdatePermission = hasPermission('site', `${site.id}`, 'site-plan-update');

  const { data: currentUserData } = useCurrentUser();
  const currentUserId = currentUserData?.id ?? null;

  const trackingData: SiteTrackingData = {
    account_id: site.account.id as number,
    organization_id: site.organizationId,
    site_id: site.id,
  };

  const handlePlanChange = (uiPosition: UiPosition) => {
    click(Event.CLICK_CHANGE_PLAN, uiPosition, trackingData);
    if (isBillingV3Enabled(currentUserData?.accounts?.[0])) {
      navigateTo(CHECKOUT);
    } else {
      navigateTo(BILLING.path);
    }
  };

  const gotoSiteDashboardOverview = () => {
    click(Event.NAVIGATE_TO_DASHBOARD_OVERVIEW, UiPosition.SITE_ITEM, trackingData);
    navigateTo(BASE.path, { siteId: site.id });
  };

  const actions = (
    <>
      {!isMobile && hasSitePlanUpdatePermission && (
        <S.ChangePlanButton
          kind="secondary"
          size="s"
          onClick={() => handlePlanChange(UiPosition.PLAN_BUTTON)}
        >
          {t('change-plan')}
        </S.ChangePlanButton>
      )}
      <SiteListAction
        icon="transfer"
        text={t('actions.transfer')}
        tooltipText={t('actions.transfer')}
        isAdmin={hasSitesUpdatePermission}
        isMobile={isMobile}
        onClick={() => {
          click(Event.CLICK_TRANSFER_SITE, UiPosition.SITE_ITEM, trackingData);
          setTransferSite(true);
        }}
      />
      <S.SettingsIcon>
        <SiteListAction
          icon="cog"
          text={t('actions.settings')}
          tooltipText={t('actions.settings')}
          isAdmin={hasSitesUpdatePermission}
          isMobile={isMobile}
          onClick={() => {
            click(Event.CLICK_SITE_SETTINGS, UiPosition.SITE_ITEM, trackingData);
            navigateTo(SETTINGS.path, { siteId: site.id });
          }}
        />
      </S.SettingsIcon>
      <SiteDeleteIcon
        isOwner={isAccountOwner(currentUserId, site.account)}
        sitesTotal={sitesTotal}
        /**
         * FIXME: this is a quick hack to support blocking deletion of sites for multisku products,
         * but it introduces a bug where you are unable to delete an Ask plan within a fair downgrade period
         *
         * TODO: We should fix this as part of the initiative to add true support for Billing 3.0 within the Sites&Orgs flows
         * https://hotjar.atlassian.net/browse/MP-2556
         */
        sitePlans={[site.plan, site.effectiveAskPlan]}
        isAdmin={hasSitesDeletePermission}
        isMobile={isMobile}
        onClick={() => {
          click(Event.CLICK_DELETE_SITE, UiPosition.SITE_ITEM, trackingData);
          setDeleteSite(true);
        }}
      />
    </>
  );

  return (
    <>
      <S.Row hover>
        <S.Cell link onClick={gotoSiteDashboardOverview}>
          {site.id}
        </S.Cell>
        <S.Cell
          link
          onClick={gotoSiteDashboardOverview}
          css={css`
            flex-direction: row;
          `}
        >
          <Tooltip text={site.url}>
            <S.TruncateText>{site.name}</S.TruncateText>
          </Tooltip>
        </S.Cell>
        <S.Cell>
          <TrackingCodeState
            lastData={site.lastData}
            isMobile={isMobile}
            onClick={(action) => {
              const eventName =
                action === 'install'
                  ? Event.CLICK_INSTALL_TRACKING_CODE
                  : Event.CLICK_VIEW_TRACKING_CODE;
              click(eventName, UiPosition.SITE_ITEM, trackingData);
              onTrackingCodeClick(site);
            }}
          />
        </S.Cell>
        <S.Cell>
          <EnforceSiteMfaSwitch site={site} isSSOEnabled={isSSOEnabled} />
        </S.Cell>
        <S.Cell data-testid="plan-cell">
          <S.Grid>
            {[
              { name: analyticsProductName, plan: site.effectivePlan },
              { name: feedbackProductName, plan: site.effectiveAskPlan },
            ].map(({ name, plan }) => {
              const activeActionRequired =
                site.isTwoSkusActionRequired && name === feedbackProductName;
              const endOfGracePeriod = getEndOfGracePeriod(site.account);

              return (
                <Fragment key={name}>
                  <span>{name}</span>
                  {activeActionRequired && (
                    <span>
                      <S.ActionRequiredBadge size="s" kind="custom">
                        {tRevenue('migrate-to-two-skus.action-required')}
                      </S.ActionRequiredBadge>
                      &nbsp;
                      <Tooltip
                        text={
                          site.lockPlan
                            ? tRevenue('revenue:migrate-to-two-skus.grace-period-locked', {
                                endOfGracePeriod,
                              })
                            : tRevenue('revenue:migrate-to-two-skus.grace-period-regular', {
                                endOfGracePeriod,
                              })
                        }
                      >
                        <Icon icon="info" inline />
                      </Tooltip>{' '}
                    </span>
                  )}
                  {!activeActionRequired && (
                    <PlanBadge
                      kind={plan ?? 'basic'}
                      size="s"
                      onClick={
                        hasSitePlanUpdatePermission
                          ? () => handlePlanChange(UiPosition.PLAN_TAG)
                          : undefined
                      }
                    />
                  )}
                </Fragment>
              );
            })}
          </S.Grid>
        </S.Cell>
        <S.Cell
          align="right"
          css={css`
            flex-direction: row;
            align-items: center;
          `}
        >
          {isMobile ? (
            <DropdownMenu content={actions}>
              <IconButton
                size="s"
                kind="ghost"
                icon="menu_kebab"
                description={t('actions.settings')}
                tooltipText={t('actions.settings')}
              />
            </DropdownMenu>
          ) : (
            <>{actions}</>
          )}
        </S.Cell>
      </S.Row>

      <DeleteSiteModal
        site={site}
        isOpen={deleteSite}
        onSuccess={() => setDeleteSite(false)}
        onCancel={() => setDeleteSite(false)}
      />
      {transferSite && (
        <TransferSiteModal
          site={site}
          opened={transferSite}
          handleSuccess={() => setTransferSite(false)}
          handleCancel={() => setTransferSite(false)}
        />
      )}
    </>
  );
}
